import React, { useEffect, useState } from "react";
import api, { errMsg } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { CalendarClock, Loader2, Play, Pause, Trash2, Plus, Info } from "lucide-react";
import { C, StatusBadge } from "./reportsUi";

const FREQ = ["daily", "weekly", "monthly"];

export default function SchedulesDialog({ open, onOpenChange, category, filters, builder, tabLabel }) {
  const [items, setItems] = useState([]);
  const [recipients, setRecipients] = useState([]);
  const [senderStatus, setSenderStatus] = useState("");
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState("");
  const [freq, setFreq] = useState("weekly");
  const [recipient, setRecipient] = useState("__me__");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: s }, { data: r }] = await Promise.all([
        api.get("/reports/center/schedules"),
        api.get("/reports/center/email-recipients"),
      ]);
      setItems(s.schedules || []); setSenderStatus(s.sender_status || "");
      setRecipients(r.recipients || []);
    } catch { toast.error("Could not load schedules."); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (open) { load(); setName(`${tabLabel} — ${freq}`); } /* eslint-disable-next-line */ }, [open]);

  const create = async () => {
    if (!name.trim()) { toast.error("Give the schedule a name."); return; }
    setSaving(true);
    try {
      const f = { ...(filters || {}) };
      if (category === "builder" && builder) f.report_type = builder.report_type;
      const body = { name: name.trim(), category, filters: f, frequency: freq };
      if (recipient !== "__me__") body.recipient_user_id = recipient;
      await api.post("/reports/center/schedules", body);
      toast.success("Schedule saved (future-ready).");
      setName(""); load();
    } catch (e) { toast.error(errMsg(e, "Could not save schedule.")); }
    finally { setSaving(false); }
  };

  const toggle = async (s) => {
    try { await api.patch(`/reports/center/schedules/${s.id}`, { status: s.status === "active" ? "paused" : "active" }); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const del = async (s) => {
    if (!window.confirm("Delete this schedule?")) return;
    try { await api.delete(`/reports/center/schedules/${s.id}`); setItems((x) => x.filter((i) => i.id !== s.id)); toast.success("Deleted."); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="rc-schedules-dialog" aria-describedby={undefined}>
        <DialogHeader><DialogTitle className="flex items-center gap-2"><CalendarClock size={18} style={{ color: C.navy }} /> Scheduled reports</DialogTitle></DialogHeader>

        <div className="flex items-start gap-2 rounded-lg px-3 py-2 text-[12.5px]" style={{ backgroundColor: "rgba(183,121,31,0.12)", color: C.amber }} data-testid="rc-sender-status">
          <Info size={15} className="mt-0.5 shrink-0" />
          <span>{senderStatus || "Recurring report sender not connected yet"} — schedules are saved and ready; automatic delivery will begin once the sender is enabled.</span>
        </div>

        <div className="rounded-lg border p-3 space-y-2.5" style={{ borderColor: C.line }}>
          <div className="text-[12px] font-semibold uppercase tracking-wide" style={{ color: C.slate }}>New schedule (based on current {tabLabel} report + filters)</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
            <div><Label className="text-[11px]">Name</Label><Input data-testid="rc-sched-name" value={name} onChange={(e) => setName(e.target.value)} className="h-9 text-[13px]" /></div>
            <div><Label className="text-[11px]">Frequency</Label>
              <Select value={freq} onValueChange={setFreq}><SelectTrigger data-testid="rc-sched-freq" className="h-9 text-[13px]"><SelectValue /></SelectTrigger>
                <SelectContent>{FREQ.map((f) => <SelectItem key={f} value={f} className="capitalize">{f}</SelectItem>)}</SelectContent></Select>
            </div>
            <div className="sm:col-span-2"><Label className="text-[11px]">Recipient</Label>
              <Select value={recipient} onValueChange={setRecipient}><SelectTrigger data-testid="rc-sched-recipient" className="h-9 text-[13px]"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="__me__">Myself</SelectItem>{recipients.filter((r) => !r.is_self).map((r) => <SelectItem key={r.id} value={r.id}>{r.name} · {r.email_masked}</SelectItem>)}</SelectContent></Select>
            </div>
          </div>
          <Button data-testid="rc-sched-create" size="sm" disabled={saving} onClick={create} className="h-9 gap-1 bg-[#9C1E22] hover:bg-[#861A1D] text-white">{saving ? <Loader2 className="animate-spin" size={14} /> : <Plus size={14} />} Save schedule</Button>
        </div>

        <div className="space-y-2 max-h-[35vh] overflow-y-auto">
          {loading ? <div className="py-6 flex justify-center"><Loader2 className="animate-spin" style={{ color: C.navy }} /></div> :
            items.length === 0 ? <p className="text-[13px] text-center py-4" style={{ color: C.slate }}>No schedules yet.</p> :
              items.map((s) => (
                <div key={s.id} data-testid={`rc-sched-row-${s.id}`} className="flex items-center justify-between gap-2 rounded-lg border p-2.5" style={{ borderColor: C.line }}>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-[13px]" style={{ color: C.navy }}>{s.name}</span>
                      <StatusBadge label={s.status} tone={s.status === "active" ? C.green : C.slate} />
                      <span className="text-[11px] capitalize" style={{ color: C.slate }}>{s.frequency}</span>
                    </div>
                    <div className="text-[11.5px]" style={{ color: C.slate }}>{s.category} → {s.recipient_masked}</div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button data-testid={`rc-sched-toggle-${s.id}`} size="sm" variant="outline" onClick={() => toggle(s)} className="h-8 gap-1 border-[#D6E0E7]" style={{ color: C.navy }}>
                      {s.status === "active" ? <><Pause size={13} /> Pause</> : <><Play size={13} /> Resume</>}
                    </Button>
                    <button data-testid={`rc-sched-del-${s.id}`} onClick={() => del(s)} className="text-[#9C1E22] hover:opacity-70 p-1.5"><Trash2 size={15} /></button>
                  </div>
                </div>
              ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
